import { Dispatch, UnknownAction } from "@reduxjs/toolkit";
import { IAxiosDefaultDataObject } from "../../config";
import BaseApp, { IBaseApp, IBaseAppDataDisplayerConfig, IBaseAppDynamicBasePage, IBaseAppFormConfig, IGridDataAdditional } from "../BaseApp";
import { addNewBrand, deleteBrand, setSelectedBrands, updateAllBrands, updateBrand, updateBrandsLoadingStatus, updateBrandsPagination, updateBrandsSearch, updateBrandsSearchParams, useReturnAllBrands, useReturnBrandsLoadingStatus, useReturnBrandsPagination, useReturnBrandsSearch, useReturnBrandsSearchParams, useReturnSelectedBrands } from "./brandsSlice";
import { GridDataColumns, IGridDataConfig } from "../../components/GridData";
import { ICellRendererParams } from "ag-grid-community";
import { Link } from "react-router-dom";

/**
 * Brand Model
 */
export interface IBrandModel {
    id: number,
    title: string,
    thumbnail: string,
    created_by: number,
    created_by_name: string,
    date_created: string,
    last_updated: string,
    is_active: boolean,
}

export const EmptyBrandModel: IBrandModel = {
    id: 0,
    title: "",
    thumbnail: "",
    created_by: 0,
    created_by_name: "",
    date_created: "",
    last_updated: "",
    is_active: false,
}

/**
 * Brands App
 */
export default class BrandsApp extends BaseApp implements IBaseApp {

    appName = "brands"
    appBaseEndPoint = "/brands"

    // customListEndPoint: string = this.appBaseEndPoint

    messages = {
        create: "Create a new brand",
        createShort: "New brand",
        edit: "Edit brand",
        editShort: "Edit",
        delete: "Delete brand",
        deleteShort: "Delete",
        view: "View brand",
        nothingFound: "No brands have been found.",
    }

    elemPrefix: string = "brands_"

    dataDisplayer: IBaseAppDataDisplayerConfig = {
        columns: [
            {
                id: "thumbnail",
                type: "thumbnail",
                label: "Thumbnail"
            },
            {
                id: "title",
                type: "text",
                label: "Title"
            },
        ],
        notionFound: {
            title: "Nothing...",
            message: "No brand have been found :("
        }
    }

    formConfig: IBaseAppFormConfig = {
        form: {
            name: "brands_form",
            elements: [
                {
                    id: this.elemPrefix + "title",
                    xs: 12,
                    type: "TextField",
                    label: "Title",
                    fullWidth: true
                },
                {
                    id: this.elemPrefix + "thumbnail",
                    xs: 12,
                    type: "ImageUploader",
                    label: "Thumbnail",
                    fullWidth: true
                },
            ]
        },
        sectionBox: {
            showTitle: true,
            title: "Details",
            description: "Fill in the brand details"
        },
        requiredFields: [
            {
                id: this.elemPrefix + "title",
                message: "Title is required!"
            },
        ]
    }

    /**
     * Redux
     */
    redux = {
        updateItem: (item: IAxiosDefaultDataObject, dispatch: Dispatch<UnknownAction>) => dispatch(updateBrand((item as IBrandModel))),
        addNewItem: (item: IAxiosDefaultDataObject, dispatch: Dispatch<UnknownAction>) => dispatch(addNewBrand((item as IBrandModel)))
    }

    /**
     * Dynamic Base Page
     */
    dynamicBasePage: IBaseAppDynamicBasePage = {
        items: useReturnAllBrands,
        pagination: useReturnBrandsPagination,
        loading: useReturnBrandsLoadingStatus,
        search: useReturnBrandsSearch,
        updateLoadingStatus: (status: boolean, dispatch: Dispatch<UnknownAction>) => dispatch(updateBrandsLoadingStatus({ loading: status })),
        deleteItem: (id: number, dispatch: Dispatch<UnknownAction>) => dispatch(deleteBrand({ id: id })),
        addNewItem: (item, dispatch) => dispatch(addNewBrand((item as IBrandModel))),
        updateItem: (item, dispatch) => dispatch(updateBrand((item as IBrandModel))),
        updateAllItems: (items, dispatch) => dispatch(updateAllBrands((items as IBrandModel[]))),
        updateSearch: (search: string, dispatch: Dispatch<UnknownAction>) => dispatch(updateBrandsSearch({ search: search })),
        updateItemsPagination: (pagination, dispatch) => dispatch(updateBrandsPagination(pagination)),
        itemsActionOptions: [
            {
                icon: "edit",
                label: "Edit",
                link: "/panel/brands/edit/[id]",
                onClick: (rawItem) => {
                    // navigate("/panel/items/edit/" + rawItem.id)
                },
            },
            {
                icon: "show",
                label: "View",
                link: "/panel/brands/view/[id]",
                onClick: (rawItem) => {
                    // navigate("/panel/items/view/" + rawItem.id)
                },
            },
        ],
        // filterForm: [],
        // customHeaderOptionActionButtons: [],
        // viewItemTemplateLink: "",
        // disableAutoViewItemLink: false,
        // hidePageHeadSection: false,
        // customTitlePageHeadSection: "",
        // disableSearch: false,
        // customPageHeadSectionActionButtons: []
    }

  /**
     * Grid data
     */
    gridDataColumns: GridDataColumns = [
        // {
        //     field: "thumbnail",
        //     headerName: "thumbnail",
        //     type: "thumbnail",
        //     resizable: false,
        //     width: 110,
        // },
        // {
        //     field: "title",
        //     headerName: "Title",
        //     width: 250,
        //     cellRenderer: (params: ICellRendererParams<IBrandModel>) => {
        //         const item = params.data

        //         if (!item){
        //             return params.value
        //         }

        //         return(
        //             <div className="grid-data-current-item-location-box">
        //                 <Link className="data-grid-link" to={`/panel/brands/view/${item.id}`}>
        //                     <h3 className="title">{item.title}</h3>
        //                 </Link>
        //             </div>
        //         )
        //     }
        // },
        {
            field: "title",
            headerName: "Brand",
            type: "title",
            width: 350,
            minWidth: 350,
            cellRendererParams: {
                appClass: () => new BrandsApp(),
                enableQuickPreview: true,
            }
        },
        {
            field: "date_created",
            headerName: "Date Created",
            type: "date",
            width: 250,
        }
    ]
    useGridData: boolean = true
    gridConfig: IGridDataConfig = {
        viewItemLink: "/panel/brands/view/[id]",
        editItemLink: "/panel/brands/edit/[id]",
    }
    gridDataEssentials: IGridDataAdditional  = {
        selectedItems: useReturnSelectedBrands,
        searchParams: useReturnBrandsSearchParams,
        actionButtons: [],
        headerOptions: [],
        setSelectedItems: (items, dispatch) => dispatch(setSelectedBrands(items)),
        updateItemSearchParams: (params, dispatch) => dispatch(updateBrandsSearchParams(params))
    }

}